"use client"

import React, { useState, useEffect, useId, useMemo, useCallback, useRef, useImperativeHandle, startTransition } from 'react'
import { useSearchParams } from 'next/navigation'
import Sentry from '@/lib/sentry-safe'
import { Search, X, ChevronDown } from 'lucide-react'
import { Input } from '@/components/ui/input'
import { type Category } from '@/lib/supabase'
import { fetchCategories } from '@/lib/api/client'
import { modelData, type ModelInfo } from '@/lib/model-data'
import { CategoryOverflowRow } from '@/components/ui/tls/category-overflow-row'
import { writeSoundsUrl } from '@/app/sounds/write-sounds-url'

interface SearchFiltersProps {
  onSearch: (filters: FilterState) => void
  initialFilters?: Partial<FilterState>
  categories?: Category[]
  categoryCounts?: Record<string, number>
  selectedCategory?: string
  onCategoryChange?: (category: string) => void
  /**
   * Opens the ONE filter sheet (FilterRail's) — the mobile half of the DS
   * FilterRow contract ("on mobile the same component becomes a Sheet trigger
   * with an applied-count badge"). Below `lg` this trigger replaces the
   * category pill row entirely; there is never a second category affordance.
   */
  onOpenCategorySheet?: () => void
  /**
   * Canonical full Tesla model name ("Model S", "Model 3", …) from the
   * FilterRail ↔ bar sync boundary (see app/sounds/use-filter-sync.ts). When
   * provided, keeps this bar's internal filters.model aligned with rail
   * changes so a later keystroke's debounced onSearch(filters) call doesn't
   * revert a rail-driven model change back to a stale value.
   */
  model?: string
  /** Editor's-picks toggle state, mirrored from the FilterRail (TES rail ↔ bar sync). */
  picksOnly?: boolean
  /**
   * The "Showing 12 of 1,400" position line. Rendered on the list-header row
   * this component owns, so the count and the sort control sit on one line
   * exactly as the DS Library template lays them out.
   */
  resultsSummary?: React.ReactNode
  onPicksOnlyChange?: (picksOnly: boolean) => void
  /**
   * Clears EVERY filter surface at once. Required whenever the page owns
   * filter state outside this bar (category/model/picks also live in the
   * filter sheet): clearing via the individual per-facet callbacks instead
   * re-broadcasts whichever facet the page had not cleared yet, which left
   * "Clear all" showing the old category's result set.
   */
  onClearFilters?: () => void
  className?: string
  ref?: React.Ref<SearchFiltersHandle>
}

export interface FilterState {
  query: string
  category: string
  model: string
  sortBy: 'created_at' | 'downloads' | 'title' | 'duration'
  sortOrder: 'asc' | 'desc'
}

const DEFAULT_FILTERS: FilterState = {
  query: '',
  category: 'All',
  model: '',
  sortBy: 'downloads',
  sortOrder: 'desc'
}

export const SORT_OPTIONS = [
  { value: 'downloads_desc', sortBy: 'downloads' as const, sortOrder: 'desc' as const, label: 'Most downloaded' },
  { value: 'created_at_desc', sortBy: 'created_at' as const, sortOrder: 'desc' as const, label: 'Newest' },
  { value: 'title_asc', sortBy: 'title' as const, sortOrder: 'asc' as const, label: 'A → Z' },
  { value: 'duration_asc', sortBy: 'duration' as const, sortOrder: 'asc' as const, label: 'Shortest' },
  { value: 'duration_desc', sortBy: 'duration' as const, sortOrder: 'desc' as const, label: 'Longest' },
  { value: 'downloads_asc', sortBy: 'downloads' as const, sortOrder: 'asc' as const, label: 'Least downloaded' },
  { value: 'created_at_asc', sortBy: 'created_at' as const, sortOrder: 'asc' as const, label: 'Oldest' },
  { value: 'title_desc', sortBy: 'title' as const, sortOrder: 'desc' as const, label: 'Z → A' },
]

function readFiltersFromUrl(get: (key: string) => string | null): FilterState {
  const sortOption = SORT_OPTIONS.find(option => option.value === get('sort'))
  return {
    query: get('q') || get('search') || '',
    category: get('category') || 'All',
    model: get('model') || '',
    sortBy: sortOption?.sortBy ?? DEFAULT_FILTERS.sortBy,
    sortOrder: sortOption?.sortOrder ?? DEFAULT_FILTERS.sortOrder,
  }
}

function filtersMatch(left: FilterState, right: FilterState): boolean {
  return left.query === right.query &&
    left.category === right.category &&
    left.model === right.model &&
    left.sortBy === right.sortBy &&
    left.sortOrder === right.sortOrder
}

const MODEL_OPTIONS: Array<{ value: string; label: string; slug: string; info?: ModelInfo }> = [
  { value: '', label: 'All models', slug: '' },
  ...Object.entries(modelData).map(([slug, info]) => ({
    value: info.name,
    label: info.name,
    slug,
    info,
  })),
]

const activeFilterClassName =
  'border-[color:color-mix(in_srgb,var(--tls-brand)_28%,var(--tls-border-subtle))] bg-[color:color-mix(in_srgb,var(--tls-brand)_10%,var(--tls-surface))] text-[var(--tls-fg-strong)]'
const neutralFilterClassName =
  'border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface)] text-[var(--tls-fg-muted)] hover:bg-[var(--tls-surface-hover)] hover:text-[var(--tls-fg-strong)]'
const activeDropdownOptionClassName =
  'bg-[color:color-mix(in_srgb,var(--tls-brand)_10%,var(--tls-surface))] text-[var(--tls-fg-strong)]'

export type SearchFiltersHandle = {
  focusSearch: () => void
}

// eslint-disable-next-line react-doctor/no-giant-component -- complex filter bar (search, categories, sort, model, mobile layout); splitting would require extensive prop drilling
// eslint-disable-next-line react-doctor/rerender-memo-with-default-value -- initialFilters/categoryCounts/className defaults are safe; this is not a memo'd component
// eslint-disable-next-line react-doctor/prefer-useReducer -- UI toggle state (dropdown open/close) is independent; combining into a reducer would obscure intent without benefit
export function SearchFilters({
  onSearch,
  initialFilters = {},
  categories: initialCategories,
  categoryCounts = {},
  selectedCategory,
  onCategoryChange,
  onOpenCategorySheet,
  model,
  picksOnly = false,
  onPicksOnlyChange,
  onClearFilters,
  resultsSummary,
  className = '',
  ref,
}: SearchFiltersProps) {
  // eslint-disable-next-line react-doctor/nextjs-no-use-search-params-without-suspense -- this component is always rendered inside a Suspense boundary by its parent (sounds page)
  const searchParams = useSearchParams()
  // Stable wrapper keeps URL-sync dependencies explicit without exposing the
  // mutable method binding to the effect.
  const searchParamsGet = useCallback((key: string) => searchParams.get(key), [searchParams])
  const urlFilters = useMemo(() => readFiltersFromUrl(searchParamsGet), [searchParamsGet])
  const urlFilterKey = JSON.stringify(urlFilters)
  const hasUrlFilterState = ['q', 'search', 'category', 'model', 'sort'].some(key => searchParams.has(key))

  // eslint-disable-next-line react-doctor/no-initialize-state, react-doctor/no-derived-state -- preserve non-URL seeds while URL filters take precedence
  const [filters, setFilters] = useState<FilterState>(() => ({
    ...DEFAULT_FILTERS,
    ...initialFilters,
    ...(hasUrlFilterState ? urlFilters : {}),
  }))

  const lastSyncedUrlFilterKeyRef = useRef(urlFilterKey)
  const skipNextFilterEffectRef = useRef(false)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const [categories, setCategories] = useState<Category[]>(initialCategories ?? [])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const suggestionsContainerRef = useRef<HTMLDivElement>(null)
  const suggestionItemRefs = useRef<(HTMLButtonElement | null)[]>([])

  useImperativeHandle(ref, () => ({
    focusSearch: () => {
      searchInputRef.current?.focus()
    }
  }), [])
  const [sortDropdownOpen, setSortDropdownOpen] = useState(false)
  const [modelDropdownOpen, setModelDropdownOpen] = useState(false)
  const sortDropdownRef = useRef<HTMLDivElement>(null)
  const modelDropdownRef = useRef<HTMLDivElement>(null)
  const sortTriggerRef = useRef<HTMLButtonElement>(null)
  const modelTriggerRef = useRef<HTMLButtonElement>(null)
  const sortItemRefs = useRef<(HTMLButtonElement | null)[]>([])
  const modelItemRefs = useRef<(HTMLButtonElement | null)[]>([])
  const uid = useId()
  const sortTriggerId = `${uid}-sort-trigger`
  const modelTriggerId = `${uid}-model-trigger`
  const onSearchRef = useRef(onSearch)
  const isInitialMountRef = useRef(true)

  // Roving-focus keyboard handling shared by the sort and model menus:
  // ArrowUp/Down/Home/End move focus between menuitems, Escape closes the
  // menu and returns focus to its trigger button.
  const handleMenuKeyDown = useCallback((
    event: React.KeyboardEvent,
    itemRefs: React.MutableRefObject<(HTMLButtonElement | null)[]>,
    closeMenu: () => void,
    triggerRef: React.RefObject<HTMLButtonElement | null>
  ) => {
    const items = itemRefs.current.filter((el): el is HTMLButtonElement => el !== null)
    if (items.length === 0) return
    const currentIndex = items.findIndex(el => el === document.activeElement)

    if (event.key === 'Escape') {
      event.preventDefault()
      closeMenu()
      triggerRef.current?.focus()
    } else if (event.key === 'ArrowDown') {
      event.preventDefault()
      items[currentIndex < 0 ? 0 : (currentIndex + 1) % items.length]?.focus()
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      items[currentIndex <= 0 ? items.length - 1 : currentIndex - 1]?.focus()
    } else if (event.key === 'Home') {
      event.preventDefault()
      items[0]?.focus()
    } else if (event.key === 'End') {
      event.preventDefault()
      items[items.length - 1]?.focus()
    }
  }, [])

  // Focus the first menu item whenever a dropdown opens, so ArrowDown from
  // the trigger (or a mouse click) lands keyboard users inside the menu.
  useEffect(() => {
    if (!sortDropdownOpen) return
    const t = setTimeout(() => sortItemRefs.current[0]?.focus(), 0)
    return () => clearTimeout(t)
  }, [sortDropdownOpen])

  useEffect(() => {
    if (!modelDropdownOpen) return
    const t = setTimeout(() => modelItemRefs.current[0]?.focus(), 0)
    return () => clearTimeout(t)
  }, [modelDropdownOpen])

  const handleSuggestionKeyDown = useCallback((event: React.KeyboardEvent, index: number) => {
    const items = suggestionItemRefs.current.filter((el): el is HTMLButtonElement => el !== null)
    if (event.key === 'Escape') {
      event.preventDefault()
      setShowSuggestions(false)
      searchInputRef.current?.focus()
    } else if (event.key === 'ArrowDown') {
      event.preventDefault()
      items[(index + 1) % items.length]?.focus()
    } else if (event.key === 'ArrowUp') {
      event.preventDefault()
      if (index === 0) {
        searchInputRef.current?.focus()
      } else {
        items[index - 1]?.focus()
      }
    }
  }, [])

  useEffect(() => {
    onSearchRef.current = onSearch
  }, [onSearch])

  // Browser history changes the URL without remounting this component. Keep
  // its controls and the parent result state aligned with the restored URL.
  // eslint-disable-next-line react-doctor/no-adjust-state-on-prop-change -- URL navigation is an external state change
  useEffect(() => {
    if (lastSyncedUrlFilterKeyRef.current === urlFilterKey) return
    lastSyncedUrlFilterKeyRef.current = urlFilterKey

    if (filtersMatch(filters, urlFilters)) return

    skipNextFilterEffectRef.current = true
    setFilters(urlFilters)
    onSearchRef.current(urlFilters)
  }, [filters, urlFilterKey, urlFilters])

  // Keep internal filters.model aligned with the externally-synced `model`
  // prop (FilterRail changes). Without this, a rail model change only shows
  // up in `activeModel` for display — the next debounced onSearch(filters)
  // fire (e.g. from a search-box keystroke) would still send the *stale*
  // internal filters.model and silently revert the rail's change.
  useEffect(() => {
    if (model === undefined) return
    startTransition(() => {
      setFilters(prev => (prev.model === model ? prev : { ...prev, model }))
    })
  }, [model])

  // Same contract for category. The filter sheet writes category through
  // `selectedCategory`; without this the bar's internal filters.category kept
  // the stale value and the next debounced onSearch silently reverted the
  // sheet's choice (the exact bug the `model` effect above documents).
  useEffect(() => {
    if (selectedCategory === undefined) return
    startTransition(() => {
      setFilters(prev => (prev.category === selectedCategory ? prev : { ...prev, category: selectedCategory }))
    })
  }, [selectedCategory])

  useEffect(() => {
    if (initialCategories && initialCategories.length > 0) return
    const loadCategories = async () => {
      try {
        const data = await fetchCategories()
        setCategories(data)
      } catch (error) {
        Sentry.captureException(error, { tags: { context: 'SearchFilters', action: 'loadData' } })
      }
    }
    loadCategories()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const clickOutsideHandlerRef = useRef<((event: MouseEvent) => void) | null>(null)
  useEffect(() => {
    // eslint-disable-next-line react-doctor/advanced-event-handler-refs -- handler uses refs internally (sortDropdownRef/modelDropdownRef); no stale closure on state
    clickOutsideHandlerRef.current = (event: MouseEvent) => {
      if (sortDropdownRef.current && !sortDropdownRef.current.contains(event.target as Node)) {
        setSortDropdownOpen(false)
      }
      if (modelDropdownRef.current && !modelDropdownRef.current.contains(event.target as Node)) {
        setModelDropdownOpen(false)
      }
    }
  })
  useEffect(() => {
    if (!(sortDropdownOpen || modelDropdownOpen)) return
    const handler = (event: MouseEvent) => clickOutsideHandlerRef.current?.(event)
    document.addEventListener('mousedown', handler)
    return () => document.removeEventListener('mousedown', handler)
  }, [sortDropdownOpen, modelDropdownOpen])

  const updateURL = useCallback((newFilters: FilterState) => {
    // Base on the *current* URL rather than rebuilding from scratch — this
    // bar only knows about q/category/model/sort, so a from-scratch build
    // would silently drop params owned by other writers (FilterRail's
    // ?picks=1, the pager's ?page=). A filter change here still means a
    // fresh result set, so ?page= is explicitly cleared (page 1).
    const params = new URLSearchParams(searchParams.toString())
    if (newFilters.query) params.set('q', newFilters.query)
    else params.delete('q')
    params.delete('search') // legacy alias, never re-emitted
    if (newFilters.category !== 'All') params.set('category', newFilters.category)
    else params.delete('category')
    if (newFilters.model) params.set('model', newFilters.model)
    else params.delete('model')

    const sortValue = `${newFilters.sortBy}_${newFilters.sortOrder}`
    if (sortValue !== 'downloads_desc') params.set('sort', sortValue)
    else params.delete('sort')

    params.delete('page')

    const queryString = params.toString()
    const newUrl = queryString ? `/sounds?${queryString}` : '/sounds'
    writeSoundsUrl(newUrl)
  }, [searchParams])

  // Held in a ref so the debounced URL-sync effect below can depend on `filters`
  // alone. `updateURL` closes over `searchParams`, so its identity changes on
  // every URL change — keeping it as an effect dependency made the effect fire
  // (and re-push a page-stripped URL) for URL changes this bar did not cause.
  const updateURLRef = useRef(updateURL)
  useEffect(() => {
    updateURLRef.current = updateURL
  }, [updateURL])

  const searchSuggestions = useMemo(() => {
    if (filters.query.length > 2) {
      return categories.filter(cat =>
        cat.name.toLowerCase().includes(filters.query.toLowerCase())
      ).slice(0, 5)
    }
    return []
  }, [filters.query, categories])

  const derivedShowSuggestions = searchSuggestions.length > 0 && filters.query.length > 2

  // Push the debounced filter state out to the parent + the URL. Deliberately
  // keyed on `filters` ONLY: `updateURL` strips `?page=`, so depending on its
  // (searchParams-derived) identity made *any* URL change re-run this effect and
  // 300ms later push `/sounds` with the pager's `?page=N` deleted. The pager
  // then mirrored its unchanged page back in, and the two writers ping-ponged
  // ~3x/second until Safari threw `SecurityError: Attempt to use
  // history.replaceState() more than 100 times per 30 seconds` (TES-1872).
  // eslint-disable-next-line react-doctor/prefer-use-effect-event -- useEffectEvent is not yet stable in this project's React version
  useEffect(() => {
    if (skipNextFilterEffectRef.current) {
      skipNextFilterEffectRef.current = false
      return
    }

    if (isInitialMountRef.current) {
      isInitialMountRef.current = false
      onSearchRef.current(filters)
      return
    }

    const timeoutId = setTimeout(() => {
      onSearchRef.current(filters)
      updateURLRef.current(filters)
    }, 300)

    return () => clearTimeout(timeoutId)
  }, [filters])

  const handleFilterChange = useCallback(<K extends keyof FilterState>(
    key: K,
    value: FilterState[K]
  ) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }, [setFilters])

  const handleCategorySelect = useCallback((categoryName: string) => {
    setFilters(prev => ({ ...prev, category: categoryName }))
    onCategoryChange?.(categoryName)
  }, [setFilters, onCategoryChange])

  // The sheet-trigger badge counts *applied facets* — never available ones,
  // and never sort (sort is not a filter: it has its own always-visible
  // control on the list header row, per the DS Library template).
  const activeCategory = selectedCategory ?? filters.category
  const activeModel = model ?? filters.model

  const activeFilterCount = useMemo(() => {
    let count = 0
    if (activeCategory !== 'All') count++
    if (activeModel) count++
    if (picksOnly) count++
    return count
  }, [activeCategory, activeModel, picksOnly])

  const allCategoryCount = useMemo(
    () => Object.values(categoryCounts).reduce((sum, count) => sum + count, 0),
    [categoryCounts]
  )

  const hasActiveSearch = filters.query.length > 0
  const hasAnyFilter = activeFilterCount > 0 || hasActiveSearch

  const clearAllFilters = useCallback(() => {
    setFilters(DEFAULT_FILTERS)
    // One atomic reset when the page owns the other filter surface; the
    // per-facet fallback keeps this component usable on its own.
    if (onClearFilters) onClearFilters()
    else {
      onCategoryChange?.('All')
      onPicksOnlyChange?.(false)
    }
    setShowSuggestions(false)
    setSortDropdownOpen(false)
    setModelDropdownOpen(false)
  }, [onClearFilters, onCategoryChange, onPicksOnlyChange])

  return (
    <>
      {/* Sticky filter bar. The <search> landmark IS the sticky box: a
          sticky element can only travel within its parent's box, and when
          the landmark wrapped both this bar and the sort row below, the
          parent was 216px tall and the bar could move 85px, never stick.
          The sort row is not a filter (see below), so it sits outside the
          landmark as a sibling. */}
      <search
        aria-label="Sound library filters"
        className={`sticky top-[60px] md:top-[64px] z-30 -mx-4 border-b px-4 sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8 ${className ?? ''}`}
        style={{
          background: 'color-mix(in srgb, var(--tls-canvas) 96%, transparent)',
          borderColor: 'var(--tls-border-subtle)',
        }}
      >
        <div className="max-w-7xl mx-auto">
          {/* Search row */}
          <div className="flex items-center gap-2 pt-4 pb-3 md:gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 z-10 size-4 -translate-y-1/2 text-[var(--tls-fg-subtle)]" />
              <Input
                ref={searchInputRef}
                data-search-hotkey-target
                placeholder="Search sounds… (press /)"
                value={filters.query}
                onChange={(e) => handleFilterChange('query', e.target.value)}
                onFocus={() => derivedShowSuggestions && setShowSuggestions(true)}
                onBlur={(e) => {
                  const next = e.relatedTarget as Node | null
                  if (next && suggestionsContainerRef.current?.contains(next)) return
                  setTimeout(() => setShowSuggestions(false), 200)
                }}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowDown' && showSuggestions && derivedShowSuggestions) {
                    e.preventDefault()
                    suggestionItemRefs.current[0]?.focus()
                  } else if (e.key === 'Escape' && showSuggestions) {
                    e.preventDefault()
                    setShowSuggestions(false)
                  }
                }}
                className="h-[46px] rounded-[var(--ls-radius-0)] border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface)] pl-9 pr-10 text-[var(--tls-fg-strong)] placeholder:text-[var(--tls-fg-subtle)]"
                aria-label="Search sounds"
              />
              {filters.query && (
                <button type="button"
                  onClick={() => handleFilterChange('query', '')}
                  className="absolute right-2 top-1/2 flex size-7 -translate-y-1/2 items-center justify-center rounded-[var(--radius-pill)] text-[var(--tls-fg-muted)] transition-colors hover:bg-[var(--tls-surface-hover)] hover:text-[var(--tls-fg-strong)]"
                  aria-label="Clear search"
                >
                  <X className="size-3.5" />
                </button>
              )}

              {/* Search Suggestions */}
              {showSuggestions && derivedShowSuggestions && (
                <div
                  ref={suggestionsContainerRef}
                  className="dropdown-enter dropdown-origin-center absolute z-40 mt-2 w-full overflow-hidden rounded-[var(--ls-radius-0)] border bg-[var(--tls-surface)]"
                  style={{
                    borderColor: 'var(--tls-border-subtle)',
                  }}
                >
                  {searchSuggestions.map((category, index) => (
                    <button type="button"
                      key={category.id || index}
                      ref={(el) => { suggestionItemRefs.current[index] = el }}
                      onMouseDown={() => {
                        handleFilterChange('query', category.name)
                        handleCategorySelect(category.name)
                        setShowSuggestions(false)
                      }}
                      onKeyDown={(e) => handleSuggestionKeyDown(e, index)}
                      onBlur={(e) => {
                        const next = e.relatedTarget as Node | null
                        if (next && suggestionsContainerRef.current?.contains(next)) return
                        setShowSuggestions(false)
                      }}
                      className="w-full border-b px-4 py-2.5 text-left text-sm text-[var(--tls-fg-muted)] transition-colors first:rounded-t-[var(--ls-radius-0)] last:rounded-b-[var(--ls-radius-0)] last:border-b-0 hover:bg-[var(--tls-surface-hover)] hover:text-[var(--tls-fg-strong)]"
                      style={{ borderColor: 'var(--tls-border-subtle)' }}
                    >
                      <Search className="mr-2 inline size-3 text-[var(--tls-fg-subtle)]" />
                      {category.name}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Desktop: Model dropdown */}
            <div className="hidden lg:block relative" ref={modelDropdownRef}>
              {(() => {
                const currentModel = MODEL_OPTIONS.find(opt => opt.value === activeModel) || MODEL_OPTIONS[0]
                return (
                  <>
                    <button type="button"
                      ref={modelTriggerRef}
                      id={modelTriggerId}
                      suppressHydrationWarning
                      onClick={() => setModelDropdownOpen(!modelDropdownOpen)}
                      onKeyDown={(e) => {
                        if (e.key === 'ArrowDown' && !modelDropdownOpen) {
                          e.preventDefault()
                          setModelDropdownOpen(true)
                        }
                      }}
                      className={`flex h-11 items-center gap-1.5 rounded-[var(--ls-radius-pill)] border px-3 py-2 text-sm font-medium transition-[background-color,border-color,color,transform] duration-[var(--motion-duration-fast)] ease-[var(--motion-ease-standard)] ${
                        activeModel
                          ? activeFilterClassName
                          : neutralFilterClassName
                      }`}
                      aria-label="Filter by Tesla model"
                      aria-haspopup="menu"
                      aria-expanded={modelDropdownOpen}
                    >
                      <span suppressHydrationWarning className="max-w-[80px] truncate">{currentModel.label}</span>
                      <ChevronDown className={`h-3.5 w-3.5 text-[var(--tls-fg-subtle)] transition-transform ${modelDropdownOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {modelDropdownOpen && (
                      <div
                        role="menu"
                        aria-labelledby={modelTriggerId}
                        onKeyDown={(e) => handleMenuKeyDown(e, modelItemRefs, () => setModelDropdownOpen(false), modelTriggerRef)}
                        className="dropdown-enter dropdown-origin-right absolute right-0 z-40 mt-2 w-52 overflow-hidden rounded-[var(--ls-radius-0)] border bg-[var(--tls-surface)]"
                        style={{
                          borderColor: 'var(--tls-border-subtle)',
                        }}
                      >
                        {MODEL_OPTIONS.map((option, index) => {
                          const isActive = activeModel === option.value
                          return (
                            <button type="button"
                              key={option.value || 'all'}
                              ref={(el) => { modelItemRefs.current[index] = el }}
                              role="menuitem"
                              tabIndex={-1}
                              onClick={() => {
                                setFilters(prev => ({ ...prev, model: option.value }))
                                setModelDropdownOpen(false)
                                modelTriggerRef.current?.focus()
                              }}
                              className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-left text-sm transition-colors ${
                                isActive
                                  ? activeDropdownOptionClassName
                                  : 'text-[var(--tls-fg-muted)] hover:bg-[var(--tls-surface-hover)] hover:text-[var(--tls-fg-strong)]'
                              }`}
                            >
                              <span>{option.label}</span>
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </>
                )
              })()}
            </div>

            {/* Desktop: editor's-picks toggle. Below `lg` this same facet
                lives inside the one filter sheet, never as a third control. */}
            {onPicksOnlyChange && (
              <button
                type="button"
                role="switch"
                aria-checked={picksOnly}
                onClick={() => onPicksOnlyChange(!picksOnly)}
                className={`hidden lg:flex h-11 items-center gap-1.5 rounded-[var(--ls-radius-pill)] border px-3 py-2 text-sm font-medium transition-[background-color,border-color,color] duration-[var(--motion-duration-fast)] ease-[var(--motion-ease-standard)] ${
                  picksOnly ? activeFilterClassName : neutralFilterClassName
                }`}
              >
                <span className="whitespace-nowrap">Editor&apos;s picks</span>
              </button>
            )}
          </div>

          {/* ── The ONE category control ─────────────────────────────────
              DS FilterRow: a wrapping pill row at `lg+` (two lines then a
              "{N} more" disclosure), and BELOW `lg` the same component
              collapses to a single Sheet trigger with an applied-count badge.
              Exactly one of the two is visible at any width. The old build
              showed a pill row AND a rail AND a sheet trigger for the same
              piece of state. The pill row still renders in the SSR HTML at
              every width (only CSS hides it), so the category links stay
              crawlable on mobile user agents too. */}
          <div className="flex items-start justify-between gap-3 pb-3">
            {/* Mobile: the single filter entry point (category + model +
                picks all live behind it). */}
            <button
              type="button"
              onClick={() => onOpenCategorySheet?.()}
              className={`lg:hidden tls-target flex items-center gap-1.5 rounded-[var(--ls-radius-pill)] border px-3 py-2 text-sm font-medium transition-[background-color,border-color,color] duration-[var(--motion-duration-fast)] ease-[var(--motion-ease-standard)] ${
                activeFilterCount > 0 ? activeFilterClassName : neutralFilterClassName
              }`}
            >
              Filters
              {activeFilterCount > 0 && (
                <span className="flex size-4 items-center justify-center rounded-[var(--radius-pill)] bg-[var(--tls-brand)] text-[0.6875rem] font-bold text-[var(--tls-brand-fg)]">
                  {activeFilterCount}
                </span>
              )}
            </button>

            {/* Desktop: the wrapping facet row. */}
            {categories.length > 0 && (
              <div className="hidden min-w-0 flex-1 lg:block">
                <CategoryOverflowRow
                  categories={categories}
                  categoryCounts={categoryCounts}
                  allCategoryCount={allCategoryCount}
                  activeCategory={activeCategory}
                  onSelectCategory={handleCategorySelect}
                  onClearAll={clearAllFilters}
                  hasActiveFilter={hasAnyFilter}
                />
              </div>
            )}

          </div>
        </div>
      </search>

      {/* List header line, straight off the DS Library template: the
          "Showing 12 of 1,400" position on the left, sort on the right.
          Deliberately OUTSIDE the sticky container above: sort belongs to the
          result list, not to the filter chrome, and a third sticky row would
          eat a third of a 390px viewport. Sort is not a filter, so it needs no
          place inside the filter sheet, which is why mobile still has exactly
          one filter entry point. */}
      <div className="mt-6 flex items-center justify-between gap-3 border-b pb-4" style={{ borderColor: 'var(--tls-border-subtle)' }}>
        <div className="flex min-w-0 items-center gap-3">{resultsSummary}</div>
          {/* min-w-0 + max-w-full: at 200% root font-size the trigger's
              label no longer fits beside the results summary in a 390px row.
              Let the trigger shrink and the label truncate instead of
              clipping the button at the container edge (WCAG 1.4.4). */}
          <div className="relative min-w-0 max-w-full shrink" ref={sortDropdownRef}>
            {(() => {
              const currentOption = SORT_OPTIONS.find(
                opt => opt.sortBy === filters.sortBy && opt.sortOrder === filters.sortOrder
              ) || SORT_OPTIONS[0]

              return (
                <>
                  <button type="button"
                    ref={sortTriggerRef}
                    id={sortTriggerId}
                    suppressHydrationWarning
                    onClick={() => setSortDropdownOpen(!sortDropdownOpen)}
                    onKeyDown={(e) => {
                      if (e.key === 'ArrowDown' && !sortDropdownOpen) {
                        e.preventDefault()
                        setSortDropdownOpen(true)
                      }
                    }}
                    className="tls-target flex max-w-full items-center gap-1.5 rounded-[var(--ls-radius-pill)] border border-[color:var(--tls-border-subtle)] bg-[var(--tls-surface)] px-3 py-2 text-sm font-medium text-[var(--tls-fg-muted)] transition-[background-color,border-color,color] duration-[var(--motion-duration-fast)] ease-[var(--motion-ease-standard)] hover:bg-[var(--tls-surface-hover)] hover:text-[var(--tls-fg-strong)]"
                    aria-label="Sort options"
                    aria-haspopup="menu"
                    aria-expanded={sortDropdownOpen}
                  >
                    <span suppressHydrationWarning className="min-w-0 truncate">{currentOption.label}</span>
                    <ChevronDown className={`h-3.5 w-3.5 shrink-0 text-[var(--tls-fg-subtle)] transition-transform ${sortDropdownOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
                  </button>

                  {sortDropdownOpen && (
                    <div
                      role="menu"
                      aria-labelledby={sortTriggerId}
                      onKeyDown={(e) => handleMenuKeyDown(e, sortItemRefs, () => setSortDropdownOpen(false), sortTriggerRef)}
                      className="dropdown-enter dropdown-origin-right absolute right-0 z-40 mt-2 w-52 overflow-hidden rounded-[var(--ls-radius-0)] border bg-[var(--tls-surface)]"
                      style={{
                        borderColor: 'var(--tls-border-subtle)',
                      }}
                    >
                      {SORT_OPTIONS.map((option, index) => {
                        const isActive = filters.sortBy === option.sortBy && filters.sortOrder === option.sortOrder
                        return (
                          <button type="button"
                            key={option.value}
                            ref={(el) => { sortItemRefs.current[index] = el }}
                            role="menuitem"
                            tabIndex={-1}
                            onClick={() => {
                              setFilters(prev => ({ ...prev, sortBy: option.sortBy, sortOrder: option.sortOrder }))
                              setSortDropdownOpen(false)
                              sortTriggerRef.current?.focus()
                            }}
                            className={`w-full flex items-center gap-2.5 px-4 py-2.5 text-left text-sm transition-colors ${
                              isActive
                                ? activeDropdownOptionClassName
                                : 'text-[var(--tls-fg-muted)] hover:bg-[var(--tls-surface-hover)] hover:text-[var(--tls-fg-strong)]'
                            }`}
                          >
                            <span>{option.label}</span>
                          </button>
                        )
                      })}
                    </div>
                  )}
                </>
              )
            })()}
          </div>
      </div>
    </>
  )
}

export default SearchFilters
